// Task #694 — Eventbrite event picker, shared by every booking flow
// (client-record-dialog, log-event-dialog, etc). Returns null entirely
// when the platform admin has the EB integration disabled — that's the
// kill-switch UX. Provides a searchable list of the workspace's
// connected events plus a paste fallback (full EB URL or raw event id)
// so reps can link an event the API hasn't surfaced yet (drafts,
// cross-account).
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

export function EventbriteEventPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const { data: status } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/eventbrite/platform-status"],
  });
  const platformEnabled = status?.enabled !== false;

  const { data, isLoading } = useQuery<{
    events: Array<{ id: string; name: string; start: string | null; status: string }>;
  }>({
    queryKey: ["/api/eventbrite/events"],
    enabled: platformEnabled,
  });
  const events = data?.events || [];

  const [search, setSearch] = useState("");
  const [pasteValue, setPasteValue] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events.slice(0, 50);
    return events.filter((e) => e.name.toLowerCase().includes(q) || e.id.includes(q)).slice(0, 50);
  }, [events, search]);

  const valueInList = useMemo(() => events.some((e) => e.id === value), [events, value]);
  useEffect(() => {
    if (value && !valueInList && !pasteValue) setPasteValue(value);
  }, [value, valueInList, pasteValue]);

  function extractEventId(raw: string): string {
    const t = (raw || "").trim();
    if (!t) return "";
    if (/^\d{6,}$/.test(t)) return t;
    const m = t.match(/(\d{6,})(?:[/?#]|$)/);
    return m ? m[1] : "";
  }

  if (!platformEnabled) return null;

  return (
    <div className="ml-6 space-y-2" data-testid="eventbrite-event-picker">
      <label className="text-xs text-muted-foreground">
        Link Eventbrite event (auto-settles when EB pays out)
      </label>
      {isLoading ? (
        <div className="h-9 px-3 flex items-center text-xs text-muted-foreground">Loading…</div>
      ) : (
        <>
          {events.length > 0 && (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events by name or id…"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                data-testid="input-eventbrite-search"
              />
              <select
                value={valueInList ? value : ""}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (e.target.value) setPasteValue("");
                }}
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                data-testid="select-eventbrite-event"
              >
                <option value="">— No EB event —</option>
                {filtered.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}{ev.start ? ` — ${new Date(ev.start).toLocaleDateString()}` : ""}
                  </option>
                ))}
              </select>
            </>
          )}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {events.length === 0
                ? "Connect Eventbrite in Integrations, or paste an event URL/id below:"
                : "Or paste an Eventbrite URL or event id (drafts, cross-account):"}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={pasteValue}
                onChange={(e) => setPasteValue(e.target.value)}
                placeholder="https://www.eventbrite.com/e/…  or  1234567890"
                className="h-9 flex-1 rounded-md border bg-background px-3 text-sm"
                data-testid="input-eventbrite-paste"
              />
              <button
                type="button"
                className="h-9 px-3 rounded-md border bg-background text-sm hover:bg-accent"
                onClick={() => {
                  const id = extractEventId(pasteValue);
                  if (id) onChange(id);
                }}
                data-testid="button-eventbrite-paste-apply"
              >
                Use
              </button>
            </div>
            {value && (
              <div className="text-xs text-muted-foreground" data-testid="text-eventbrite-current">
                Linked EB event id: <span className="font-mono">{value}</span>
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={() => { onChange(""); setPasteValue(""); }}
                  data-testid="button-eventbrite-clear"
                >
                  clear
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
